import { RouteConfig } from 'config/constants/route'
import { Token } from 'config/types'
import { ValidationError } from 'config/types/validator'
import Column from 'layout/Components/Column'
import { Trans, useTranslation } from 'react-i18next'
import Flex from 'UIKit/Box/Flex'
import Text from 'UIKit/Text'

import { useMemo } from 'react'
import styled from 'styled-components'
import { Icons } from 'svgs'
import { colors } from 'theme/colors'
import { getFullDisplayBalance } from 'utils/formatBalance'

import DepositRolloverOverview from 'views/Payment/components/DepositRollover/DepositRolloverOverview'
import TraditionalWithdrawButton from 'views/Payment/components/Withdraw/WithdrawCryptos/TraditionalWithdrawButton'
import WalletWithdrawButton from 'views/Payment/components/Withdraw/WithdrawCryptos/WalletWithdrawButton'

import { PaymentMethodEnum } from 'config/constants/payment'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { useCloseAllModal } from 'hooks/useModal'
import { useRouter } from 'hooks/useRouter'
import { RowCenter } from 'layout/Components/Row'
import { useNetwork } from 'state/app/hooks'
import ContractInfo from 'views/Payment/components/ContractInfo'
import ExtraInfo, { ExtraInfoItem } from 'views/Payment/components/ExtraInfo'
import { PaymentPageEnums } from 'views/Payment/Menu'
import SelectedNetworkItem from '../../components/SelectedNetworkItem'
import {
  AmountDisplay,
  LockBonusAmountWithdrawOverview,
  PaymentFormControl,
  PaymentFormInput,
  PaymentFormInputAmount,
  PaymentInputContainer,
  PaymentStroke,
  StickyButtonWrapper,
} from '../../components/styles'
import { useWithdrawControls, WithdrawCryptoControls, WithdrawModeEnums } from '../../hooks/useWithdraw'
import { IBonusControl } from '../../types'

const WithdrawInputErrorMessages = {
  receiveAddress: {
    [ValidationError.Required]: 'Address is required',
    [ValidationError.WrongAddressFormat]: 'Your address is invalid',
    [ValidationError.Equal]:
      'Withdrawals to deposit wallet addresses are not allowed. Please enter a different address.',
  },
  amount: {
    [ValidationError.Insufficient]: 'Insufficient balance',
    [ValidationError.LessThan]: 'The min withdrawal amount is {{value}} {{unit}}',
  },
}

interface WithdrawCryptosProps {
  onBack: () => void
  onClose: () => void
  selectedToken: Token
  bonusControl: IBonusControl
}

const WithdrawCryptos: React.FC<WithdrawCryptosProps> = ({ selectedToken, onClose, bonusControl: { bonuses } }) => {
  const { t } = useTranslation()
  const closeAllModal = useCloseAllModal()
  const router = useRouter()

  const {
    controls: {
      handler: { openDepositRolloverModal, onWithdrawSuccess },
      data: {
        isValidWithdrawToOwnerAddress,
        allowComment,
        typeComment,
        form,
        bonusWithdrawAmount,
        withdrawalAmount,
        totalMinWithdrawAmount,
        lockAmount,
        totalDepositRollover,
        withdrawFee,
        valueFormatDecimal,
        inputFormatDecimal,
      },
      status: { isFetching, isTraditionalWithdraw, isWalletWithraw, isShowDepositRollover, isShowLockAmount },
    },
  } = useWithdrawControls<WithdrawCryptoControls>({
    selectedToken,
    mode: WithdrawModeEnums.CRYPTO,
    bonuses,
  })

  const { states, controls, isValid, validateAll } = useMemo(() => form, [form])
  const { isMobile } = useMatchBreakpoints()

  const selectedNetwork = useNetwork(selectedToken.network)

  return (
    <Column gap="var(--pm-space-component, 20px)" height="100%">
      {isMobile && (
        <PaymentFormControl
          state={{
            errors: [],
            value: '',
            isDirty: false,
          }}
          formatErrorMessage={() => ''}
          label={t('Network')}
        >
          <SelectedNetworkItem
            selectedNetwork={selectedNetwork?.network}
            selectedToken={selectedToken}
            from={PaymentPageEnums.Withdraw}
          />
        </PaymentFormControl>
      )}

      {!isValidWithdrawToOwnerAddress && (
        <PaymentFormControl
          state={states.receiveAddress}
          label={t('Address')}
          formatErrorMessage={(errors) => WithdrawInputErrorMessages.receiveAddress[errors[0]]}
        >
          <PaymentFormInput
            tabIndex={1}
            control={controls.receiveAddress}
            placeholder={t('Address')}
            type="address"
            name="address"
          />
        </PaymentFormControl>
      )}

      {allowComment && (
        <PaymentFormControl
          state={states.memo}
          label={t('Destination Tag (Optional)')}
          formatErrorMessage={() => false}
        >
          <PaymentFormInput
            tabIndex={1}
            control={controls.memo}
            placeholder={t('Destination Tag')}
            type={typeComment}
            name="memo"
          />
        </PaymentFormControl>
      )}

      <Column gap="var(--pm-space-label, 8px)">
        <PaymentFormControl
          state={states.amount}
          label={t('Amount')}
          formatErrorMessage={(errors) =>
            t(WithdrawInputErrorMessages.amount[errors[0]], {
              value: totalMinWithdrawAmount,
              unit: selectedToken.name,
            })
          }
          disabled={isFetching}
        >
          <PaymentFormInputAmount
            tabIndex={1}
            control={controls.amount}
            name="amount"
            disabled={isFetching}
            value={states.amount.value}
            token={selectedToken}
            errors={!isFetching && states.amount.errors}
            validators={controls.amount.validators}
            onErrorChanged={controls.amount.onErrorChanged}
            onValueChanged={controls.amount.onValueChanged}
            max={withdrawalAmount}
          />
        </PaymentFormControl>

        <AmountDisplay title="Available" amount={withdrawalAmount} currency={selectedToken} />
      </Column>

      {bonusWithdrawAmount.gt(0) && (
        <PaymentInputContainer
          style={{
            fontSize: 'var(--pm-text-note-size)',
          }}
          alignItems="flex-start"
          height="auto"
          padding="12px"
          background="var(--pm-card) !important"
        >
          <RowCenter minWidth="20px" minHeight="20px">
            <Icons.InfoCircleIcon
              style={{
                transform: 'scale(1.3)',
              }}
              fill={colors.textSubtle}
            />
          </RowCenter>
          <Column gap="8px">
            <Text fontSize="1em" color="var(--pm-text-note-color)">
              <Trans>
                Please kindly remember to complete the wagering process in order to receive your bonus before proceeding
                with your withdrawal.
              </Trans>
            </Text>

            <Text
              style={{
                textTransform: 'capitalize',
              }}
              fontSize="1em"
              color="white"
            >
              <Trans>Your bonus</Trans> <>{getFullDisplayBalance(bonusWithdrawAmount, 0, 5)}</>{' '}
              <>{selectedToken.name}</>
            </Text>

            <StyledTextButton
              onClick={() => {
                router.push(RouteConfig.BonusCenter)
                closeAllModal()
              }}
            >
              <Text fontSize="1em" color="var(--pm-label-color)">
                <Trans>Details</Trans>
              </Text>
              <Icons.ChevronRightIcon width="4.5px" fill={colors.textTertiary} />
            </StyledTextButton>
          </Column>
        </PaymentInputContainer>
      )}

      <PaymentStroke />

      <ExtraInfo>
        <ExtraInfoItem title="Contract Information" hide={!selectedToken.isNative || !selectedToken.address}>
          <ContractInfo from={PaymentMethodEnum.Withdraw} token={selectedToken} />
        </ExtraInfoItem>

        <ExtraInfoItem side="right" title="Your withdraw amount">
          {withdrawalAmount.gte(states.amount.value) && states.amount.value ? (
            <>
              {inputFormatDecimal.toString(10)} {selectedToken.name}
            </>
          ) : (
            '-'
          )}
        </ExtraInfoItem>
        <ExtraInfoItem title="Withdraw fee">
          {withdrawFee && withdrawFee?.gt(0) ? (
            <>
              {withdrawFee.toString(10)} {selectedToken.code}
            </>
          ) : (
            <Trans>No Fee</Trans>
          )}
        </ExtraInfoItem>
        <ExtraInfoItem title="You will get">
          {withdrawalAmount.gte(valueFormatDecimal) &&
          withdrawalAmount.gte(states.amount.value) &&
          valueFormatDecimal.gt(0) ? (
            <>
              {valueFormatDecimal.toString(10)} {selectedToken.name}
            </>
          ) : (
            '-'
          )}
        </ExtraInfoItem>

        {isShowLockAmount && (
          <LockBonusAmountWithdrawOverview lockAmount={lockAmount} onClose={onClose} selectedToken={selectedToken} />
        )}
        {isShowDepositRollover && (
          <DepositRolloverOverview
            openDepositRollovermodal={openDepositRolloverModal}
            selectedToken={selectedToken}
            totalDepositRollover={totalDepositRollover}
          />
        )}
      </ExtraInfo>

      <StickyButtonWrapper>
        {isTraditionalWithdraw ? (
          <TraditionalWithdrawButton
            fee={withdrawFee?.toString() || ''}
            selectedToken={selectedToken}
            toAddress={states.receiveAddress.value}
            validateAll={validateAll}
            onSuccess={onWithdrawSuccess}
            value={inputFormatDecimal.toString(10)}
            memo={states.memo.value}
            disabled={!isValid || !states.amount.value || isFetching}
            tabIndex={1}
          />
        ) : (
          isWalletWithraw && (
            <WalletWithdrawButton
              selectedToken={selectedToken}
              validateAll={validateAll}
              onSuccess={onWithdrawSuccess}
              value={inputFormatDecimal.toString(10)}
              disabled={!isValid || !states.amount.value}
              toAddress={states.receiveAddress.value}
              memo={states.memo.value}
              tabIndex={1}
            />
          )
        )}
      </StickyButtonWrapper>
    </Column>
  )
}

const StyledTextButton = styled(Flex)`
  cursor: pointer;
  align-items: center;
  gap: 8px;

  svg {
    fill: ${({ theme }) => theme.colors.textAlt1};
  }

  &:hover {
    ${Text} {
      color: ${({ theme }) => theme.colors.text} !important;
    }

    svg {
      fill: ${({ theme }) => theme.colors.text};
    }
  }
`

export default WithdrawCryptos
